/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { ButtonSpinner } from '../_common/ButtonSpinner';
import { type CommonProps, type TooltipObjectProps } from '../_common/types';
import { ChevronDownIconOutline } from '../icons/generated/heroIcons/ChevronDownIconOutline';
import { Tooltip } from '../tooltip';

type SelectIconButtonProps = {
  /** Content displayed in the button */
  children?: React.ReactNode;
  /** Size of button */
  size?: 'small' | 'medium' | 'large';
  /** If the button is doing something Async, it will display a loading spinner   */
  isLoading?: boolean;
  /** Whether the button is in disabled state */
  isDisabled?: boolean;
  /** If the button is opened */
  isOpen: boolean;
  /** Callback function to be called when the button is clicked */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /**
   * A string that will be shown as a tooltip when hovering over the button. It also acts as an aria-label.
   * {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label}
   */
  description: string | null;
  /**
   * Props for the tooltip component.
   */
  tooltipProps?: TooltipObjectProps;

  /** Accessible message for screen readers when the button is in a loading state */
  loadingMessage?: string;
};

export const SelectIconButton = ({
  children,
  size = 'medium',
  isDisabled = false,
  isLoading = false,
  isOpen = false,
  className,
  description,
  tooltipProps,
  onClick,
  style,
  htmlAttributes,
  ref,
  loadingMessage = 'Loading',
  ...restProps
}: CommonProps<'button', SelectIconButtonProps>) => {
  const classes = classNames('ndl-select-icon-btn', className, {
    'ndl-active': isOpen,
    'ndl-disabled': isDisabled,
    'ndl-large': size === 'large',
    'ndl-loading': isLoading,
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
  });

  const isInteractable = !isDisabled && !isLoading;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // If the button is disabled or loading, we don't want to call the onClick handler. The loading is only aria-disabled, so we need this.
    if (!isInteractable) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Tooltip
      hoverDelay={{
        close: 0,
        open: 500,
      }}
      type="simple"
      // We disable the tooltip if the button is disabled or open, so it doesn't interfere with a menu open
      isDisabled={description === null || isDisabled || isOpen === true}
      shouldCloseOnReferenceClick
      {...tooltipProps?.root}
    >
      <Tooltip.Trigger {...tooltipProps?.trigger} hasButtonWrapper>
        <button
          type="button"
          ref={ref}
          className={classes}
          style={style}
          disabled={isDisabled}
          aria-disabled={!isInteractable}
          aria-label={description ?? undefined}
          aria-expanded={isOpen}
          onClick={handleClick}
          {...restProps}
          {...htmlAttributes}
        >
          <div className="ndl-select-icon-btn-inner">
            <div className="ndl-icon">{children}</div>
          </div>
          <ChevronDownIconOutline
            className={classNames('ndl-select-icon-btn-icon', {
              'ndl-select-icon-btn-icon-open': isOpen === true,
            })}
          />
          {isLoading && (
            <ButtonSpinner loadingMessage={loadingMessage} size={size} />
          )}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Content {...tooltipProps?.content}>
        {description}
      </Tooltip.Content>
    </Tooltip>
  );
};
